import { CommonModule } from '@angular/common'
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withXhr } from '@angular/common/http'
import { APP_INITIALIZER, ErrorHandler, NgModule, provideZoneChangeDetection } from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { BrowserModule, provideClientHydration, withEventReplay } from '@angular/platform-browser'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { NgxDatatableModule } from '@swimlane/ngx-datatable'

import { SortablejsModule } from 'nxt-sortablejs'
import { environment } from 'environments/environment'

import { EffectsModule } from '@ngrx/effects'
import {
  FullRouterStateSerializer, RouterStateSerializer, StoreRouterConnectingModule
} from '@ngrx/router-store'
import { Store, StoreModule, USER_PROVIDED_META_REDUCERS } from '@ngrx/store'
import { StoreDevtoolsModule } from '@ngrx/store-devtools'

import {
  CustomSerializer, effects, reducers
} from '@app/core/store'

import { getMetaReducers } from '@app/shared/store/metaReducers'

import { UsersModule } from '@app/users/users.module'
// FIX Imported because of New Quote Component if available in navbar
import { AppRoutingModule } from '@app/core/app-routing.module'

import { AppComponent } from '@app/core/app.component'
import { FooterComponent } from '@app/core/containers/footer.component'
import { NavigationComponent } from '@app/core/containers/navigation.component'
import { MenuComponent } from '@app/core/containers/menu.component'
import { NotFoundPageComponent } from '@app/core/containers/not-found-page'
import { FeedbackPageComponent } from './containers/feedback-page/feedback-page.component'

import { ComponentsModule } from '@app/core/components'

import { PipesModule } from '@app/shared/pipes'
import { MyCurrencyPipe } from '@app/shared/pipes/my-currency.pipe'
import { RoundPipe } from '@app/shared/pipes/round.pipe'

import {
  AccountDataService, ApiService, CoreComponentsModule, DieselService, MessagingService, ProfilesService, RedirectService, SelectizeService, SharedModule, SocketService, StoreUtilService, TypeService, AuthorizeNetService
} from '@app/shared'

import {
  AccountThreadService, ExpensesService, ExpirationService, FileService, HistoryLogService, NotificationService, TaskService, TaskBoardService,TaskProjectService, FillerRequestService, TemplateService, ToastHelper, FeedbackService, loaderInterceptor,
  GoogleService, WorkflowService, AvailabilityService, HaulexGeneralNotificationService
} from '@app/core/services'

import { getErrorHandler } from '@app/core/services/sentry.service' // Do not remove this line
// import { AccountFormModalComponent } from '@app/crm/components/account-form-modal-component'
import { AuthService, UserService } from '@app/users/services'


import { CompanyMessageContainerComponent } from './containers/company-message-container/company-message-container.component'

import { ExpensesModule } from '@app/expense/expenses.module'
import { ModalsModule } from '@app/modals/modals.module'
import { FromNowPipe } from '@app/shared/pipes/from-now.pipe'
import { SidebarModule } from '@app/sidebar/sidebar.module'
import { tokenInterceptor } from '@app/users/services/token.interceptor'
import { ShopModule } from '@app/shop/shop.module'
import { NgxPopperjsModule } from 'ngx-popperjs'
import { GoogleMapsModule } from '@angular/google-maps'
import { ModalService } from '@app/shared/services/open-modal.service'
import { DashboardsModule } from '@app/dashboards/dashboards.module'
import { SidebarService } from './services/sidebar.service'
import { ScheduleModule } from '@app/schedule/schedule.module'
import { Router } from '@angular/router'
import { PublicSocketService } from '@app/shared/services/public-socket.service'

const COMPONENTS = [
  AppComponent,
  NavigationComponent,
  FooterComponent,
  MenuComponent,
  NotFoundPageComponent,
  CompanyMessageContainerComponent,
  FeedbackPageComponent,
]

export function checkAndRedirect(router: Router, redirectService: RedirectService, store: Store): () => Promise<void> {
  return async () => {
    const userIndex = String(redirectService.getUrlIndex())

    const currentHash = window.location.hash
    const hasNumber = /^#\/\d+\//.test(currentHash)
    if (!hasNumber) {
      const newHash = `#/${userIndex}${currentHash.substring(1)}`
      window.location.replace(newHash)
    }
  }
}
// const surl = "ws://dev-ws.dieseldisptach.com";
// 'http://localhost:8080'
// const config: SocketIoConfig = { url: surl, options: {} };
@NgModule({ declarations: COMPONENTS,
    bootstrap: [AppComponent], imports: [CommonModule,
        BrowserModule,
        BrowserAnimationsModule,
        GoogleMapsModule,
        AppRoutingModule,
        // NgProgressModule.forRoot(),
        StoreModule.forRoot({}, {
            runtimeChecks: {
                strictStateImmutability: false,
                strictActionImmutability: false,
                strictStateSerializability: false,
                strictActionSerializability: false,
                strictActionWithinNgZone: false,
            },
        }),
        SidebarModule,
        ModalsModule,
        StoreModule.forFeature('core', reducers),
        EffectsModule.forRoot([...effects]),
        StoreRouterConnectingModule.forRoot({
            serializer: FullRouterStateSerializer,
        }),
        // DEV ONLY — never instrument in production.
        //
        // instrument() posts every dispatched action AND a full RootState snapshot over postMessage
        // to the Redux DevTools extension, which serializes and retains them. `logOnly` does NOT
        // prevent that — it only disables time-travel dispatching — and `maxAge` bounds NgRx's own
        // history, not the extension's. Snapshots of a store this size land in V8's large-object
        // space (>128KB each), where they are unreclaimable for as long as the extension holds them.
        //
        // After the lock SSE rework raised the steady action rate (every platform-wide lock event,
        // the 15s tab-wide heartbeat, reconnect seeds), developers — the only people with the
        // extension installed — accumulated 2.5-3.8GB of large objects over a day and the renderer
        // died with 0xe0000008 (Chrome OOM, V8 CALL_AND_RETRY_LAST). Seven crash dumps, all with
        // large-object space at multiple GB while old space sat at 69-138MB. Customers were never
        // affected because they have no devtools extension to feed.
        ...(environment.production ? [] : [StoreDevtoolsModule.instrument({
            maxAge: 25,
            // trace: true,
            // traceLimit: 75
        })]),
        NgxDatatableModule,
        FormsModule,
        ReactiveFormsModule,
        SortablejsModule.forRoot({ animation: 150 }),
        ComponentsModule,
        PipesModule,
        UsersModule,
        SharedModule,
        CoreComponentsModule,
        ScheduleModule,
        ShopModule,
        ExpensesModule,
        DashboardsModule,
        NgxPopperjsModule
        // SocketIoModule.forRoot(config) ,
    ], providers: [
        {
          provide: APP_INITIALIZER,
          useFactory: checkAndRedirect,
          deps: [Router, RedirectService],
          multi: true
        },
        ApiService,
        AuthService,
        TypeService,
        ProfilesService,
        ModalService,
        UserService,
        DieselService,
        SocketService,
        PublicSocketService,
        SidebarService,
        MessagingService,
        SelectizeService,
        AccountDataService,
        MyCurrencyPipe,
        FromNowPipe,
        RoundPipe,
        ToastHelper,
        FileService,
        TemplateService,
        NotificationService,
        AccountThreadService,
        HistoryLogService,
        TaskService,
        TaskBoardService,
        TaskProjectService,
        FillerRequestService,
        AvailabilityService,
        SidebarService,
        ExpirationService,
        ExpensesService,
        FeedbackService,
        AuthorizeNetService,
        GoogleService,
        RedirectService,
        WorkflowService,
        HaulexGeneralNotificationService,
        { provide: RouterStateSerializer, useClass: CustomSerializer },
        {
            provide: USER_PROVIDED_META_REDUCERS,
            deps: [StoreUtilService],
            useFactory: getMetaReducers
        },
        provideHttpClient(withXhr(), withInterceptors([tokenInterceptor, loaderInterceptor])),
        { provide: ErrorHandler, useFactory: getErrorHandler },    ] })
export class AppModule {
  constructor() {
    console.log('[Module] : Loaded AppModule')
  }
}
